import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Dropdown,
  Form,
  Row,
  Space,
  Spin,
  Tabs,
  Upload,
} from "antd";
import { observer } from "mobx-react-lite";
import { match } from "ts-pattern";

import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import {
  FormInput,
  FormItem,
  FormSelect,
  FormSwitch,
  FormTextArea,
} from "@/ui/_common_/form-helpers";
import { FormSpinner } from "@/ui/_common_/form-spinner";
import Selector from "@/ui/_common_/selector";

import { EditBannerFormState } from "./edit-form.state";

interface Props {
  state: EditBannerFormState;
}

export const EditBannerFormView = observer<Props>(
  ({
    state: {
      id,

      form,

      query,
      mutation,
      desktopImageQuery,
      desktopExtrabetImageQuery,
      mobileImageQuery,

      banner,

      type,
      actionType,

      handleSubmit,
      handleTabRemove,

      providerSelectorStore,
      gameSelectorStore,

      tagsOptions,
      bonusesOptions,

      usedLanguageCodes,
      unusedLanguageCodes,
      addLanguageMenu,
    },
  }) => {
    if (query.isIdle || query.isPending) {
      return <FormSpinner />;
    }

    if (query.isRejected) {
      return <ErrorsFormatter queries={[query]} />;
    }

    return (
      <Form
        size="small"
        labelCol={{ span: 8 }}
        labelAlign="left"
        wrapperCol={{ span: 16 }}
        layout="horizontal"
        onFinish={handleSubmit}
      >
        <ErrorsFormatter queries={[mutation]} />
        <FormItem form={form} path="type" label={"Type"}>
          <FormSelect
            form={form}
            path="type"
            options={[
              {
                label: "Simple",
                value: "simple",
              },
              {
                label: "Text",
                value: "text",
              },
              {
                label: "Bonus",
                value: "bonus",
              },
            ]}
          />
        </FormItem>
        <Tabs
          className="tabs--container"
          type="editable-card"
          size="small"
          destroyInactiveTabPane={true}
          onEdit={(key, action) => {
            if (action === "remove") {
              handleTabRemove(key as string);
            }
          }}
          items={usedLanguageCodes.map((code) => ({
            key: code,
            label: code.toUpperCase(),
            closable: usedLanguageCodes.length > 1 && code !== "en",
            children: (
              <>
                <FormItem form={form} path={`titles.${code}`} label={"Title"}>
                  <FormInput
                    form={form}
                    path={`titles.${code}`}
                    placeholder={"Enter title"}
                  />
                </FormItem>
                {type !== "simple" ? (
                  <FormItem
                    form={form}
                    path={`subtitles.${code}`}
                    label={"Text"}
                  >
                    <FormTextArea
                      form={form}
                      path={`subtitles.${code}`}
                      placeholder={"Enter text"}
                    />
                  </FormItem>
                ) : (
                  <FormItem
                    form={form}
                    path={`subtitles.${code}`}
                    label={"Subtitle"}
                  >
                    <FormInput
                      form={form}
                      path={`subtitles.${code}`}
                      placeholder={"Enter subtitle"}
                    />
                  </FormItem>
                )}
                <FormItem form={form} path={`buttons.${code}`} label={"Button"}>
                  <FormInput
                    form={form}
                    path={`buttons.${code}`}
                    placeholder={"Enter Button"}
                  />
                </FormItem>
              </>
            ),
          }))}
          hideAdd={true}
          tabBarExtraContent={{
            right: !!unusedLanguageCodes.length && (
              <Dropdown menu={addLanguageMenu} trigger={["click"]}>
                <Button size="small">
                  <PlusOutlined />
                </Button>
              </Dropdown>
            ),
          }}
        />
        <Form.Item label={"Desktop image (1924x360px)"}>
          <Space direction="vertical">
            <Upload
              listType="picture-card"
              showUploadList={false}
              customRequest={desktopImageQuery.submit}
              className="picture-card--banner"
            >
              {banner.backgroundDesktop ? (
                <Spin spinning={desktopImageQuery.isPending}>
                  <img
                    src={`/media/banner?id=${id}&type=desktop&cache=${Date.now()}`}
                    style={{ width: "100%", maxHeight: 80 }}
                  />
                </Spin>
              ) : (
                <div>
                  {desktopImageQuery.isPending ? (
                    <LoadingOutlined />
                  ) : (
                    <PlusOutlined />
                  )}
                  <div>{"Upload"}</div>
                </div>
              )}
            </Upload>
            <ErrorsFormatter queries={[desktopImageQuery]} />
          </Space>
        </Form.Item>
        <Form.Item label={"Desktop extrabet image (613x252px)"}>
          <Space direction="vertical">
            <Upload
              listType="picture-card"
              showUploadList={false}
              customRequest={desktopExtrabetImageQuery.submit}
              className="picture-card--banner"
            >
              {banner.backgroundDesktopExtrabet ? (
                <Spin spinning={desktopExtrabetImageQuery.isPending}>
                  <img
                    src={`/media/banner?id=${id}&type=desktop_extrabet&cache=${Date.now()}`}
                    style={{ width: "100%", maxHeight: 80 }}
                  />
                </Spin>
              ) : (
                <div>
                  {desktopExtrabetImageQuery.isPending ? (
                    <LoadingOutlined />
                  ) : (
                    <PlusOutlined />
                  )}
                  <div>{"Upload"}</div>
                </div>
              )}
            </Upload>
            <ErrorsFormatter queries={[desktopExtrabetImageQuery]} />
          </Space>
        </Form.Item>
        <Form.Item label={"Mobile image (750x340px)"}>
          <Space direction="vertical">
            <Upload
              listType="picture-card"
              showUploadList={false}
              customRequest={mobileImageQuery.submit}
              className="picture-card--banner"
            >
              {banner.backgroundMobile ? (
                <Spin spinning={mobileImageQuery.isPending}>
                  <img
                    src={`/media/banner?id=${id}&type=mobile&cache=${Date.now()}`}
                    style={{ width: "100%", maxHeight: 80 }}
                  />
                </Spin>
              ) : (
                <div>
                  {mobileImageQuery.isPending ? (
                    <LoadingOutlined />
                  ) : (
                    <PlusOutlined />
                  )}
                  <div>{"Upload"}</div>
                </div>
              )}
            </Upload>
            <ErrorsFormatter queries={[mobileImageQuery]} />
          </Space>
        </Form.Item>
        <FormItem form={form} path="actionType" label={"Action"}>
          <FormSelect
            form={form}
            path="actionType"
            options={[
              {
                label: "None",
                value: null,
              },
              {
                label: "URL",
                value: "url",
              },
              {
                label: "Provider",
                value: "provider",
              },
              {
                label: "Tag",
                value: "tag",
              },
              {
                label: "Game",
                value: "game",
              },
              {
                label: "Category",
                value: "category",
              },
            ]}
          />
        </FormItem>
        {match(actionType)
          .with("url", () => (
            <FormItem form={form} path="actionValue" label={"URL"}>
              <FormInput
                form={form}
                path="actionValue"
                placeholder={"Enter URL"}
              />
            </FormItem>
          ))
          .with("provider", () => (
            <FormItem form={form} path="actionValue" label={"Provider"}>
              <Selector
                store={providerSelectorStore}
                placeholder={"Select provider"}
              />
            </FormItem>
          ))
          .with("game", () => (
            <FormItem form={form} path="actionValue" label={"Game"}>
              <Space direction="vertical">
                <Selector
                  store={providerSelectorStore}
                  placeholder={"Select provider"}
                />
                <Selector
                  store={gameSelectorStore}
                  placeholder={"Select game"}
                />
              </Space>
            </FormItem>
          ))
          .with("tag", () => (
            <FormItem form={form} path="actionValue" label={"Tag"}>
              <FormSelect
                form={form}
                path="actionValue"
                options={tagsOptions}
                placeholder={"Select tag"}
              />
            </FormItem>
          ))
          .with("category", () => (
            <FormItem form={form} path="actionValue" label={"Category ID"}>
              <FormInput
                form={form}
                path="actionValue"
                placeholder={"Enter category ID"}
              />
            </FormItem>
          ))
          .with(null, () => (
            <div style={{ display: "none" }}>
              <input
                type="hidden"
                {...form.register("actionValue", { value: null })}
              />
            </div>
          ))
          .otherwise(() => null)}
        {type === "bonus" ? (
          <FormItem form={form} path="bonusId" label={"Bonus"}>
            <FormSelect
              form={form}
              path="bonusId"
              options={bonusesOptions}
              placeholder={"Select bonus"}
            />
          </FormItem>
        ) : (
          <div style={{ display: "none" }}>
            <input
              type="hidden"
              {...form.register("bonusId", { value: null })}
            />
          </div>
        )}
        <FormItem form={form} path="order" label={"Order"}>
          <FormInput form={form} path="order" placeholder={"Enter order"} />
        </FormItem>
        <FormItem form={form} path="isActive" label={"Visible"}>
          <FormSwitch form={form} path="isActive" />
        </FormItem>
        <Row justify="end" gutter={12}>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={mutation.isPending}
            >
              {"Save"}
            </Button>
          </Col>
        </Row>
      </Form>
    );
  },
);
